import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MapPin, Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import ShareButtons from '@/components/ShareButtons';

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await api.get(`/public/projects/${slug}`);
        setProject(response.data);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const nextImage = () => {
    if (project?.media?.length > 1) {
      setLightboxIndex((prev) => (prev + 1) % project.media.length);
    }
  };

  const prevImage = () => {
    if (project?.media?.length > 1) {
      setLightboxIndex((prev) => (prev - 1 + project.media.length) % project.media.length);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, project]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center">
        <h1 className="font-heading text-3xl mb-4">Progetto non trovato</h1>
        <Link to="/portfolio">
          <Button variant="outline">
            <ArrowLeft className="mr-2" size={18} />
            Torna al Portfolio
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="project-detail-page">
      {/* Back Button */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-8">
        <Link to="/portfolio" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-portfolio">
          <ArrowLeft size={18} className="mr-2" />
          Torna al Portfolio
        </Link>
      </div>

      {/* Header */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-6" data-testid="project-title">
            {project.title}
          </h1>
          <div className="flex flex-wrap gap-6 text-muted-foreground mb-6">
            {project.location && (
              <div className="flex items-center gap-2">
                <MapPin size={18} />
                <span>{project.location}</span>
              </div>
            )}
            {project.date && (
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{project.date}</span>
              </div>
            )}
          </div>
          {project.description && (
            <p className="text-lg text-muted-foreground max-w-3xl">{project.description}</p>
          )}
          {project.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {project.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-muted text-sm text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div className="mt-8">
            <ShareButtons title={project.title} text={project.description} />
          </div>
        </motion.div>
      </div>

      {/* Gallery */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12">
        <div className="gallery-masonry" data-testid="project-gallery">
          {project.media?.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="cursor-pointer group"
              onClick={() => openLightbox(index)}
            >
              <div className="overflow-hidden bg-muted">
                <img
                  src={item.thumbnail_url || item.url}
                  alt={item.alt_text}
                  className="w-full object-cover img-zoom"
                  loading="lazy"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mt-24 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl mb-6">Ti piace questo lavoro?</h2>
        <p className="text-muted-foreground mb-8">Parliamo del tuo progetto</p>
        <Link to="/prenota">
          <Button size="lg" data-testid="project-cta-btn">
            Richiedi un Preventivo
          </Button>
        </Link>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lightbox-backdrop flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="project-lightbox"
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
            >
              <X size={32} />
            </button>

            {project.media?.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
                >
                  <ChevronLeft size={48} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
                >
                  <ChevronRight size={48} />
                </button>
              </>
            )}

            <motion.img
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              src={project.media?.[lightboxIndex]?.url}
              alt={project.media?.[lightboxIndex]?.alt_text}
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-6 left-6 text-white">
              <p className="text-white/50 text-sm">
                {lightboxIndex + 1} / {project.media?.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
